import React from 'react';
import { Box, Drawer, IconButton, alpha, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  borderRadius,
  componentSizes,
  getControlBorder,
  getControlBorderColor,
  workspaceLayout,
} from '../../../theme/theme';
import { getWorkflowWorkspacePalette, getWorkspaceDrawerPaperSx } from './WorkflowWorkspacePrimitives';

export interface WorkspaceCollapsibleRailProps {
  children: React.ReactNode;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isMobile: boolean;
  mobileMenuOpen: boolean;
  onCloseMobileMenu: () => void;
  /** Optional explicit width for the expanded rail. Defaults to workspaceLayout.treeWidth. */
  width?: number;
  toggleButtonRef?: React.Ref<HTMLButtonElement>;
  /** Refs for spotlight tour targets. */
  tourRefs?: {
    railRef?: React.RefObject<HTMLDivElement | null>;
    toggleRef?: React.RefObject<HTMLButtonElement | null>;
  };
  sx?: SxProps<Theme>;
}

/**
 * Collapsible left navigation rail for workspace pages.
 *
 * - Desktop: inline rail with an expand/collapse toggle.
 * - Mobile: falls back to a Drawer so the navigator can overlay the content.
 * - Widths, borders, and transition timing are driven from theme tokens.
 */
export const WorkspaceCollapsibleRail: React.FC<WorkspaceCollapsibleRailProps> = ({
  children,
  collapsed,
  onToggleCollapsed,
  isMobile,
  mobileMenuOpen,
  onCloseMobileMenu,
  width = workspaceLayout.treeWidth,
  toggleButtonRef,
  tourRefs,
  sx,
}) => {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const borderColor = getControlBorderColor(theme);
  const collapsedWidth = workspaceLayout.treeWidthCollapsed;
  const railWidth = collapsed ? collapsedWidth : width;

  const toggleButton = (
    <IconButton
      ref={(node) => {
        if (typeof toggleButtonRef === 'function') {
          toggleButtonRef(node);
        } else if (toggleButtonRef) {
          // eslint-disable-next-line no-param-reassign
          toggleButtonRef.current = node;
        }
        if (tourRefs?.toggleRef && tourRefs.toggleRef.current !== node) {
          // eslint-disable-next-line no-param-reassign
          tourRefs.toggleRef.current = node;
        }
      }}
      size="small"
      onClick={onToggleCollapsed}
      aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      data-testid="workspace-rail-toggle"
      sx={{
        position: 'absolute',
        left: collapsed ? collapsedWidth - 12 : width - 12,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        transition: theme.transitions.create(['left'], {
          duration: theme.transitions.duration.standard,
          easing: theme.transitions.easing.easeOut,
        }),
        width: 24,
        height: 24,
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        border: getControlBorder(theme),
        borderRadius: '50%',
        boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.15)}`,
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderColor,
        },
      }}
    >
      {collapsed ? (
        <ChevronRight sx={{ fontSize: '0.85rem' }} />
      ) : (
        <ChevronLeft sx={{ fontSize: '0.85rem' }} />
      )}
    </IconButton>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={onCloseMobileMenu}
        PaperProps={{
          sx: [
            getWorkspaceDrawerPaperSx(theme, 'left'),
            {
              width: '85%',
              maxWidth: 320,
            },
          ],
        }}
      >
        <Box
          ref={tourRefs?.railRef}
          data-testid="workspace-rail"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      </Drawer>
    );
  }

  return (
    <>
      <Box
        ref={tourRefs?.railRef}
        data-testid="workspace-rail"
        sx={[
          {
            width: railWidth,
            minWidth: railWidth,
            maxWidth: railWidth,
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: collapsed ? 'transparent' : palette.paneBackground,
            transition: theme.transitions.create(['width', 'min-width', 'max-width', 'background-color'], {
              duration: theme.transitions.duration.standard,
              easing: theme.transitions.easing.easeOut,
            }),
            overflow: 'hidden',
            position: 'relative',
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {children}
      </Box>
      {toggleButton}
    </>
  );
};

export default WorkspaceCollapsibleRail;
